import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BellPlus, BellRing, Flame, MapPin, Music4, Camera, Trash2, Plus, Loader2, Calendar, Users, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errText, mediaUrl } from "../lib/api";
import { Loading, Empty, Avatar, CreatorName } from "../components/common";
import { Button } from "../components/ui/button";

const field = "w-full surface-2 border border-white/10 rounded-xl px-3 h-10 text-sm outline-none focus:border-gold/50";
const fmt = (iso) => new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function VenuePage() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const load = () => api.get(`/virtucity/venues/${id}`).then(({ data }) => setV(data)).catch(() => setV(false));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [id]); // eslint-disable-line
  if (v === null) return <Loading />;
  if (v === false) return <Empty icon={Music4} title="Venue not found" />;
  return (
    <div className="px-4 md:px-6 py-5 space-y-5" data-testid="venue-page">
      <Link to="/virtucity?tab=nightlife" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-gold" data-testid="venue-back"><ArrowLeft size={14} />Nightlife</Link>
      <VenueHero v={v} reload={load} />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Gallery v={v} reload={load} />
          <Sets v={v} reload={load} />
        </div>
        <div className="space-y-4">
          <CrowdMeter v={v} />
          <RecentPlays v={v} />
        </div>
      </div>
    </div>
  );
}

function VenueHero({ v, reload }) {
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(v.description || "");
  const cover = v.cover_url || v.photos?.[0]?.url;
  const follow = async () => {
    try { await (v.is_following ? api.delete(`/virtucity/venues/${v.external_id}/follow`) : api.post(`/virtucity/venues/${v.external_id}/follow`)); reload(); }
    catch (e) { toast.error(errText(e)); }
  };
  const save = async () => { try { await api.put(`/virtucity/venues/${v.external_id}/profile`, { description: desc }); setEditing(false); reload(); toast.success("Saved"); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 min-h-[260px] flex items-end" data-testid="venue-hero">
      {cover ? <img src={mediaUrl(cover)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(251,113,133,.25),transparent_60%),radial-gradient(ellipse_at_bottom_left,rgba(74,142,255,.25),transparent_60%)] bg-[#07070c]" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
      <div className="relative p-6 md:p-8 w-full">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] font-semibold">
          {v.is_open ? <span className="text-rose-400 flex items-center gap-1" data-testid="venue-live"><Flame size={12} />LIVE NOW</span> : <span className="text-muted-foreground" data-testid="venue-closed">CLOSED</span>}
          <span className="text-gold uppercase">{v.kind}</span>
        </div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl mt-2" data-testid="venue-name">{v.name}</h1>
        {v.location && <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><MapPin size={13} />{v.location}</div>}
        {editing ? (
          <div className="mt-3 max-w-xl"><textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className={`${field} h-auto py-2`} data-testid="venue-desc-input" />
            <div className="flex gap-2 mt-2"><Button onClick={save} className="btn-gold text-black rounded-full h-8" data-testid="venue-desc-save">Save</Button><Button variant="outline" onClick={() => setEditing(false)} className="rounded-full h-8 border-white/15">Cancel</Button></div></div>
        ) : v.description && <p className="text-sm mt-3 max-w-xl text-white/85" data-testid="venue-description">{v.description}</p>}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button onClick={follow} data-testid="venue-follow-btn" className={`h-10 px-5 rounded-full text-sm font-semibold flex items-center gap-2 ${v.is_following ? "btn-gold text-black" : "btn-blue"}`}>
            {v.is_following ? <><BellRing size={15} />Following</> : <><BellPlus size={15} />Follow venue</>}<span className="opacity-70">· {v.follower_count}</span>
          </button>
          {v.owner && <Link to={`/${v.owner.username}`} className="flex items-center gap-2 text-xs"><Avatar src={v.owner.avatar_url} name={v.owner.display_name} size={26} />Owned by <CreatorName user={v.owner} /></Link>}
          {v.can_manage && !editing && <button onClick={() => setEditing(true)} className="text-xs text-gold flex items-center gap-1" data-testid="venue-edit-desc"><Pencil size={12} />Edit description</button>}
        </div>
      </div>
    </div>
  );
}

function CrowdMeter({ v }) {
  const pct = v.capacity ? Math.min(100, Math.round((v.crowd / v.capacity) * 100)) : 0;
  const vibe = !v.is_open ? "Doors closed" : pct >= 85 ? "Packed" : pct >= 50 ? "Lit" : pct >= 20 ? "Warming up" : "Early";
  const dj = (v.dj || "").replace(/^dj\s+/i, "");
  return (
    <div className="surface rounded-2xl p-5" data-testid="venue-crowd-meter">
      <div className="font-semibold flex items-center gap-2"><Users size={15} className="text-gold" />Live crowd</div>
      <div className="flex items-end justify-between mt-3">
        <div className="font-display font-bold text-4xl" data-testid="venue-crowd-count">{v.is_open ? v.crowd || 0 : 0}</div>
        <div className="text-right text-xs text-muted-foreground">{v.capacity ? `of ${v.capacity} · ${pct}%` : "inside"}<div className="text-gold font-semibold">{vibe}</div></div>
      </div>
      <div className="h-3 rounded-full surface-2 overflow-hidden mt-3">
        <div className="h-full transition-[width] duration-700" style={{ width: `${v.is_open ? pct : 0}%`, background: "linear-gradient(90deg,#4A8EFF,#D4AF37,#fb7185)" }} />
      </div>
      {v.is_open && dj && <div className="text-sm mt-4">On the decks: {v.dj_user ? <Link to={`/${v.dj_user.username}`} className="text-gold font-semibold">{dj}</Link> : <span className="text-gold font-semibold">{dj}</span>}</div>}
      {v.is_open && v.now_playing && <div className="text-xs text-muted-foreground mt-1" data-testid="venue-now-playing">Now playing: {v.now_playing}</div>}
      <div className="text-[10px] text-muted-foreground mt-3">Updates live from VirtuCity</div>
    </div>
  );
}

function Gallery({ v, reload }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/virtucity/venues/${v.external_id}/photos`, { url: data.url });
      toast.success("Photo added"); reload();
    } catch (er) { toast.error(errText(er)); } finally { setBusy(false); e.target.value = ""; }
  };
  const del = async (p) => { try { await api.delete(`/virtucity/venues/${v.external_id}/photos/${p.id}`); reload(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="surface rounded-2xl p-5" data-testid="venue-gallery">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold flex items-center gap-2"><Camera size={15} className="text-gold" />Photos</div>
        {v.can_manage && <><input ref={ref} type="file" accept="image/*" hidden onChange={upload} data-testid="venue-photo-input" />
          <Button onClick={() => ref.current?.click()} disabled={busy} className="btn-gold text-black rounded-full h-8 text-xs" data-testid="venue-add-photo">{busy ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} className="mr-1" />Add photo</>}</Button></>}
      </div>
      {v.photos.length === 0 ? <p className="text-xs text-muted-foreground">No photos yet.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {v.photos.map((p) => (
            <div key={p.id} className="relative group aspect-[4/3] rounded-xl overflow-hidden" data-testid={`venue-photo-${p.id}`}>
              <img src={mediaUrl(p.url)} alt={p.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              {v.can_manage && <button onClick={() => del(p)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center opacity-0 group-hover:opacity-100" data-testid={`venue-photo-delete-${p.id}`}><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Sets({ v, reload }) {
  const [f, setF] = useState(null);
  const add = async () => {
    if (!f.dj_name || !f.starts_at) return toast.error("DJ name and start time are required");
    try {
      await api.post(`/virtucity/venues/${v.external_id}/sets`, { ...f, starts_at: new Date(f.starts_at).toISOString(), ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null, dj_username: f.dj_username || null });
      setF(null); reload(); toast.success("DJ set scheduled");
    } catch (e) { toast.error(errText(e)); }
  };
  const del = async (s) => { try { await api.delete(`/virtucity/venues/${v.external_id}/sets/${s.id}`); reload(); } catch (e) { toast.error(errText(e)); } };
  const now = Date.now();
  return (
    <div className="surface rounded-2xl p-5" data-testid="venue-sets">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold flex items-center gap-2"><Calendar size={15} className="text-gold" />Upcoming DJ sets</div>
        {v.can_manage && !f && <Button onClick={() => setF({ dj_name: "", dj_username: "", title: "", starts_at: "", ends_at: "" })} className="btn-gold text-black rounded-full h-8 text-xs" data-testid="venue-add-set"><Plus size={13} className="mr-1" />Schedule set</Button>}
      </div>
      {f && (
        <div className="surface-2 rounded-xl p-3 mb-3 grid sm:grid-cols-2 gap-2" data-testid="venue-set-form">
          <input value={f.dj_name} onChange={(e) => setF({ ...f, dj_name: e.target.value })} placeholder="DJ name" className={field} data-testid="venue-set-dj" />
          <input value={f.dj_username} onChange={(e) => setF({ ...f, dj_username: e.target.value })} placeholder="DJ's VirtuStars @username (optional)" className={field} data-testid="venue-set-dj-username" />
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Set title (optional)" className={`${field} sm:col-span-2`} data-testid="venue-set-title" />
          <label className="text-[11px] text-muted-foreground">Starts<input type="datetime-local" value={f.starts_at} onChange={(e) => setF({ ...f, starts_at: e.target.value })} className={field} data-testid="venue-set-start" /></label>
          <label className="text-[11px] text-muted-foreground">Ends<input type="datetime-local" value={f.ends_at} onChange={(e) => setF({ ...f, ends_at: e.target.value })} className={field} data-testid="venue-set-end" /></label>
          <div className="flex gap-2 sm:col-span-2"><Button onClick={add} className="btn-gold text-black rounded-full h-9" data-testid="venue-set-save">Save set</Button><Button variant="outline" onClick={() => setF(null)} className="rounded-full h-9 border-white/15">Cancel</Button></div>
        </div>
      )}
      {v.sets.length === 0 ? <p className="text-xs text-muted-foreground">No sets scheduled.</p> : v.sets.map((s) => {
        const live = new Date(s.starts_at).getTime() <= now && (!s.ends_at || new Date(s.ends_at).getTime() > now);
        return (
          <div key={s.id} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid={`venue-set-${s.id}`}>
            <div className="w-14 text-center shrink-0"><div className="text-[10px] text-muted-foreground uppercase">{new Date(s.starts_at).toLocaleDateString([], { weekday: "short" })}</div><div className="font-display font-bold">{new Date(s.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div></div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm">{s.dj_username ? <Link to={`/${s.dj_username.replace(/^@/, "")}`} className="hover:text-gold">{s.dj_name}</Link> : s.dj_name}{live && <span className="ml-2 text-[10px] text-rose-400 font-bold">LIVE</span>}</div>
              <div className="text-[11px] text-muted-foreground">{s.title ? `${s.title} · ` : ""}{fmt(s.starts_at)}{s.ends_at ? ` – ${new Date(s.ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}</div>
            </div>
            {v.can_manage && <button onClick={() => del(s)} className="text-muted-foreground hover:text-rose-400" data-testid={`venue-set-delete-${s.id}`}><Trash2 size={14} /></button>}
          </div>
        );
      })}
    </div>
  );
}

function RecentPlays({ v }) {
  return (
    <div className="surface rounded-2xl p-5" data-testid="venue-recent-plays">
      <div className="font-semibold flex items-center gap-2 mb-3"><Music4 size={15} className="text-gold" />Recently played</div>
      {v.recent_plays.length === 0 ? <p className="text-xs text-muted-foreground">Nothing played yet.</p> : v.recent_plays.map((p, i) => (
        <div key={i} className="text-sm py-1.5 flex justify-between gap-2"><span className="truncate">{p.title}<span className="text-muted-foreground"> · {p.artist || "Unknown"}</span></span><span className="text-[11px] text-muted-foreground shrink-0">{new Date(p.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span></div>
      ))}
    </div>
  );
}
